/* ==========================================================================
   分类页 QUICK VIEW —— AJAX 加载 + 抽屉填充 + 变体切换 + 标题联动
   2026-08-26 稿 V0DUwe7bwKFnumOjcXPTPR
   ==========================================================================
   骨架：snippets/amada-quickview-drawer.liquid（mode: 'assets' / 'payload'）
   外壳：assets/amada-drawer.js（右侧抽屉）+ assets/amada-modal.js（居中弹窗）
   —— 两个外壳都不在这里实现，本文件只负责「取数据 → 填进去 → 让它动起来」。

   ▍为什么走 AJAX（用户明确要求）
   分类页一屏就十几个商品，把每个商品的 quick view 都渲染进 HTML，
   首屏体积和 DOM 节点数会翻好几倍。**点了才请求**，且同一商品只请求一次（内存缓存）。

   ▍请求形态（2026-09-02 第三轮定稿：静态 section 名）
     GET `<product.url>?section_id=main-product--quick-view`
   实测四种形态（草稿主题，沙发集合 8 件商品逐个打）：
     ?view=quick-view                              200 / 688~780 KB  整页，含全站 header/footer/app
     ?view=quick-view&section_id=main              404               ⚠️ 键名不是完整 id
     ?view=quick-view&section_id=template--…__main 200 / ~210 KB     要先学到模板号才能用
     ?section_id=main-product--quick-view          200 / 158~236 KB  ← **现在用这条**
   选它的三个理由：
     ① **没有冷启动依赖** —— 不需要「先跑一次整页去学 template--<号>__main」，
        fresh session 第一次点任何商品都直接拿到片段；
     ② 体积比整页小一个数量级，且不受商品 template_suffix 影响；
     ③ 不含模板号/主题号，live 与草稿通用。
   🚨 代价：静态形态下 `section.settings` 只有 schema 默认值，读不到
      templates/product.quick-view.json。所以 quick view 的运营设置已迁到
      **全局主题设置**（config/settings_schema.json 的「AMADA QUICK VIEW 抽屉」分组），
      运营只在那一处配。改这条请求形态前先想清楚这件事。
   取不到 payload 时退回整页形态一次（section 被改名之类的兜底）。
   全部请求带 8s 超时（铁律 31：没超时的失败表现成无限转圈，看起来像 bug）。

   ▍弹窗内容（契约 A 怎么落地的 —— 关键设计，别改成直接 render）
   代理 D 的 snippets/amada-pdp-drawer-bodies.liquid 只从
   **sections/amada-pdp-drawers.liquid 那个 section 对象**的 blocks 取数。
   而 main-product--quick-view 里的 `section` 是它自己 —— 那些 blocks 在这里根本不存在，
   直接 `{% render 'amada-pdp-drawer-bodies', section: section %}` 只会得到一份空内容。
   把 amada-pdp-drawers 复制进 templates/product.quick-view.json 能解决，但那是
   **58 个 block 的重复配置**，运营改一处要记得改两处 —— 违背「只配一份内容」的初衷，不做。

   实际做法：**去商品详情页把已经渲染好的六个抽屉 body 抓回来**。
   🔄 2026-10-05（运营 10.5 表第 7/8/14 行）：抽屉内容已经是**商品级**配置——
   每个商品模板的 amada-pdp-drawers 各配各的 Size Guide / Dimensions / Seel 区块。
   原来「整个会话只抓一次、全局缓存」会让后面所有商品都显示第一个被抓的商品
   （space-styling 页全部变成 17 沙发的尺寸图）。现在改为：
     · 每个商品抓**它自己的** PDP（GET <product.url>），按 handle 缓存（内存 + sessionStorage）；
     · 抓的同时记下 PDP 上「有没有 Size Guide / Order Free Swatches 入口、哪些抽屉是空的」，
       quick view 的入口显隐照抄 PDP（PDP 有才有，PDP 没有就不出）；
     · PDP 的 Size Guide 网格为空时，PDP 自己会退回 variant_picker 配的尺寸页内容
       （assets/amada-pdp.js fillSizeGuideFallback），这里同口径搬那份内容。
   抓到前入口先藏起来，避免先闪出一个 PDP 上没有的链接；抓不到就保留兜底文案。

   ▍Seel「Worry-Free」横幅（2026-09-07 运营 #31）
   PDP 侧的做法：sections/amada-pdp-drawers.liquid 收了一个 `@app` block，
   在 snippets/amada-pdp-drawer-bodies.liquid 的 shipping 分支 panel 之后 `{% render block %}`。
   该 app block 的**服务端 HTML 只有三样东西**：
     ① `<script>window.sealAppBlock={shopDomain,productId,design_mode}</script>`
     ② `<div id="seal-pdp-block"><div class="seal-pdp-widget-container"></div></div>`
     ③ 两个 `<script type="module" defer src=".../vendor.js|pdp-widget.js">`
   横幅本体是 pdp-widget.js（React）在**客户端**画出来的。

   放到 quick view 里有三个必须处理的点：
   ① 弹窗内容是 sharedBodies 用 innerHTML 整段塞进来的 —— **innerHTML 插入的 <script> 不执行**，
      所以光把 HTML 搬过来只会得到一个空的 #seal-pdp-block。
   ② pdp-widget.js 模块执行时 `document.getElementById('seal-pdp-block')` 找到就 render 一次，
      商品 id **只在组件挂载时读一次** window.sealAppBlock.productId，
      而且没有暴露任何重渲染 / 卸载 API。
      → 换商品唯一可行的办法是**让模块重新执行一次**：用带不同 query 的地址
        （pdp-widget.js?amada-qv=<productId>）追加 `<script type="module">`，
        浏览器把它当成新的模块实例，会重新去找 #seal-pdp-block 渲染。
        它内部 `import './vendor.js'` 是相对路径、不受 query 影响，走缓存不会重下。
   ③ 同一商品第二次打开**不能再追加脚本**（会重复渲染 + 白白多一个模块实例）：
      已经渲染好的那个节点缓存在 seelRendered[productId] 里，直接 replaceWith 占位 ——
      React 树跟着 DOM 节点走，节点搬家后依然有效。
   商品 id 来自 payload 根节点的 data-product-id（snippets/amada-quickview-drawer.liquid）。
   PDP 没配 app block / Seel 改了类名或 URL 时全部静默降级：弹窗其它内容不受影响。
   横幅样式已由 amada-pdp-drawers.css 的 `.amada-pdpd-shipping .seal-pdp-widget-*` 收口，
   这里**不写任何样式**。

   ▍事件委托一律**捕获阶段**
   卡片是代理 A 的 section 渲染的、抽屉内容是 AJAX 插进来的 —— 直接绑元素必掉。
   而且主题自己也给商品卡绑了监听（theme.js:5742 的 .show-product-quick-view），
   冒泡阶段注册会被它先跑并 stopPropagation。
   ========================================================================== */
(function () {
  'use strict';
  /* 2026-10-06：只执行一次。快速预览资源改为全站加载（购物车抽屉推荐「+」开快速预览），
     PDP / 分类页自己也会挂同一个文件 → 同一页出现两个 <script src>，第二份直接让位，
     否则事件委托会绑两遍（点一次触发两次）。 */
  if (window.__amadaQuickviewLoaded) return;
  window.__amadaQuickviewLoaded = true;

  var DRAWER_ID = 'amada-quickview';
  var FETCH_TIMEOUT_MS = 8000;
  /** 稿 4:8115 起的抽屉标题两态：基础态 QUICK VIEW，任一弹窗打开后变 PRODUCT DETAILS */
  var TITLE_BASE = 'QUICK VIEW';
  var TITLE_DETAILS = 'PRODUCT DETAILS';

  /** handle → payload HTML 字符串。同一商品点第二次不再发请求。 */
  var cache = {};
  /** 当前抽屉里装的是哪个商品（避免慢请求回来把新的覆盖掉，经典图片错位同源问题） */
  var currentHandle = null;
  /** 当前商品的变体表（来自 payload 里的 JSON） */
  var variants = [];

  /* ------------------------------------------------------------------
     工具
     ------------------------------------------------------------------ */
  function drawerRoot() {
    return document.querySelector('[data-amada-drawer="' + DRAWER_ID + '"]');
  }

  function qs(sel, scope) {
    return (scope || document).querySelector(sel);
  }

  function qsa(sel, scope) {
    return Array.prototype.slice.call((scope || document).querySelectorAll(sel));
  }

  /** 带超时的 fetch：没有超时的话失败表现成无限转圈，看起来像 bug（铁律 31） */
  function fetchText(url) {
    var controller = typeof AbortController === 'function' ? new AbortController() : null;
    var timer = window.setTimeout(function () {
      if (controller) controller.abort();
    }, FETCH_TIMEOUT_MS);

    return fetch(url, {
      credentials: 'same-origin',
      signal: controller ? controller.signal : undefined
    })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.text();
      })
      .finally(function () {
        window.clearTimeout(timer);
      });
  }

  function withParams(url, extra) {
    return url + (url.indexOf('?') === -1 ? '?' : '&') + extra;
  }

  /* ------------------------------------------------------------------
     取 payload：静态 section 形态（2026-09-02 第三轮改）
     --------------------------------------------------------------------
     形态：`<商品url>?section_id=main-product--quick-view`
       · 用的是**静态 section 名**，不带 view、不带模板号 —— 不依赖「先学到
         template--<数字>__main」，因此**没有冷启动依赖**：fresh session
         第一次点任何商品都能直接拿到片段。
       · 与整页形态 `?view=quick-view` 相比体积小一个数量级
         （实测 8 件商品：整页 688~780KB vs 静态 158~236KB），
         且不受商品 template_suffix 影响。
       · 片段完整性已实测：shell / body / footer / modals / variants /
         库存三态 / 评论宿主 / 卡模板 / 灯箱 全部在。
     🚨 代价：静态形态下 `section.settings` 只有 schema 默认值，**读不到
        templates/product.quick-view.json**（实测：模板写 per_page=6，静态仍返回 4）。
        所以 quick view 的运营设置已迁到全局主题设置，见
        config/settings_schema.json 的「AMADA QUICK VIEW 抽屉」分组。
     兜底：静态形态取不到 payload（section 文件被改名等）时退回整页形态一次。
     原来那条「轻量形态 view=quick-view&section_id=template--…」已删除 ——
     它体积与静态形态相同，却多一个「必须先跑一次整页去学模板号」的依赖，没有留存价值。
     ------------------------------------------------------------------ */
  /** 静态 section 名 = sections/main-product--quick-view.liquid 的文件名 */
  var QV_SECTION = 'main-product--quick-view';
  /** pdp 抽屉 body 的全局缓存（模板级配置，与商品无关，只抓一次） */
  var sharedBodies = null;

  /** pdp 抽屉 id → quick view 弹窗里的填充位 key（契约 A 的 which 取值） */
  var BODY_MAP = {
    'pdp-overview': 'product-overview',
    'pdp-size-guide': 'size-guide',
    'pdp-dimensions': 'dimensions',
    'pdp-material-care': 'material-care',
    'pdp-shipping': 'shipping',
    'pdp-returns-warranty': 'returns-warranty',
    'pdp-order-swatches': 'order-swatches'
  };

  /** 需要连 footer 一起抓的抽屉：抽屉 id → 弹窗里的填充位 key。
      2026-09-14 组 G2：quick view 的 ORDER FREE SWATCHES 弹窗补了吸底加购按钮，
      按钮上的 `data-swatch-group` 必须与小样卡上的完全一致 —— 而卡是从 PDP 抓来的，
      group 是 **PDP 那个 section 的 id**，quick view 本地的 section 根本不是它。
      所以按钮也只能连同 PDP 的 `.amada-drawer__footer` 一起整段搬过来，
      不能在 quick view 侧用本地 section 重新渲染（否则 group 对不上，按钮永远灰着）。 */
  var FOOTER_MAP = {
    'pdp-order-swatches': 'order-swatches-footer'
  };

  /**
   * 从某个商品的 PDP 整页里收割抽屉 body 与入口显隐标记。
   * 不是 PDP 整页（没有 pdp-* 抽屉）时返回 null —— quick view 自己的响应里没有这些。
   */
  /* 与 PDP 同口径（assets/amada-pdp.js applyLegacyProductFallbacks）：
     非专属抽屉（data-amada-product-fallback）由同页旧手风琴的最终内容覆盖，
     旧手风琴先经 amada-redesign.js 的「数学粗体字符 → 普通字母」归一化。
     PDP 是页面加载后在浏览器里做的，抓回来的 HTML 里没有这一步，不补的话
     quick view 会显示 𝐔𝐩𝐠𝐫𝐚𝐝𝐞𝐝 这类字符、或显示与 PDP 不同的兜底正文。 */
  var LEGACY_ALIASES = {
    'product-overview': ['details', 'description', 'product details'],
    dimensions: ['features & specs', 'features and specs', 'specifications', 'dimensions'],
    'material-care': ['materials', 'the materials', 'material & care', 'materials & construction']
  };

  function applyLegacyFallbacks(box) {
    var accordions = qsa('.product-blocks__accordion', box);
    if (!accordions.length) return;
    if (typeof window.AmadaNormalizeMathBold === 'function') {
      accordions.forEach(function (a) {
        var c = qs('.accordion__content', a);
        if (c) {
          try { window.AmadaNormalizeMathBold(c); } catch (e) { /* 归一化失败只影响字形，不影响内容 */ }
        }
      });
    }
    qsa('[data-amada-product-fallback]', box).forEach(function (fb) {
      var aliases = LEGACY_ALIASES[fb.getAttribute('data-amada-product-fallback')];
      var target = qs('[data-amada-product-fallback-content]', fb);
      if (!aliases || !target) return;
      for (var i = 0; i < accordions.length; i++) {
        var heading = qs('.accordion__label h3, .accordion__label', accordions[i]);
        var name = heading ? heading.textContent.replace(/\s+/g, ' ').trim().toLowerCase() : '';
        if (aliases.indexOf(name) === -1) continue;
        var content = qs('.accordion__content', accordions[i]);
        if (content && content.textContent.replace(/\s+/g, '')) {
          target.innerHTML = content.innerHTML;
          return;
        }
      }
    });
  }

  function harvestSharedBodies(box) {
    if (!qs('[data-amada-drawer^="pdp-"]', box)) return null;
    applyLegacyFallbacks(box);
    var found = { __flags: { sizeGuide: false, swatches: false, empty: {} } };
    var flags = found.__flags;
    Object.keys(BODY_MAP).forEach(function (drawerId) {
      var d = qs('[data-amada-drawer="' + drawerId + '"] .amada-drawer__body', box);
      if (!d) return;
      var html = d.innerHTML.trim();
      var key = BODY_MAP[drawerId];
      if (drawerId === 'pdp-size-guide') {
        /* 网格空 → 与 PDP 同口径退回尺寸页内容（隐藏的旧弹窗 .amada-size-guide-modal） */
        var sizeGrid = d.querySelector('.amada-pdpd-sizeguide__grid');
        if (!sizeGrid || sizeGrid.children.length === 0) {
          var modal = qs('.amada-size-guide-modal', box);
          var modalHtml = modal ? modal.innerHTML.trim() : '';
          html = modalHtml.replace(/\s|&nbsp;/g, '')
            ? '<div class="amada-pdpd amada-pdpd-sizeguide"><div class="amada-pdpd-sizeguide__fallback">' + modalHtml + '</div></div>'
            : '';
        }
      }
      if (!html || (!d.textContent.replace(/\s+/g, '') && !/<img|<svg|<video|seal-pdp-block/i.test(html))) {
        flags.empty[key] = true;
        if (!html) return;
      }
      found[key] = html;
    });
    Object.keys(FOOTER_MAP).forEach(function (drawerId) {
      var f = qs('[data-amada-drawer="' + drawerId + '"] .amada-drawer__footer', box);
      if (!f) return;
      var fhtml = f.innerHTML.trim();
      if (fhtml) found[FOOTER_MAP[drawerId]] = fhtml;
    });
    /* 入口显隐照抄 PDP：PDP 上渲染了哪个入口，quick view 才出哪个 */
    flags.sizeGuide = Boolean(qs('[data-amada-size-guide]', box)) && Boolean(found['size-guide']) && !flags.empty['size-guide'];
    flags.swatches = Boolean(qs('[data-amada-drawer-open="pdp-order-swatches"]', box));
    return found;
  }

  function parse(html) {
    var box = document.createElement('div');
    box.innerHTML = html;
    return box;
  }

  /** 当前抽屉里那个商品的 PDP 抽屉内容（applySharedBodies 用它） */
  var sharedBodies = null;
  /** handle → 该商品 PDP 抽屉内容（商品级配置，绝不能跨商品复用） */
  var bodiesByHandle = {};
  /** 正在抓 / 已抓失败的商品，避免重复发整页请求 */
  var bodiesTried = {};
  /** sessionStorage 键前缀（+ handle）：逛几个页面同一商品只抓一次 PDP。
      🚨 结构一变就要升版本号，否则老会话拿到的还是旧结构。
      v5 → v6（2026-10-05 运营 10.5 #7/#8/#14）：从「全站一份」改成「每个商品一份」并带入口标记。
      v6 → v7（同日）：收割前补 PDP 的旧手风琴兜底 + 数学粗体归一化，旧缓存里是未处理的正文。 */
  var SHARED_KEY = 'amada:qv:bodies:v7:';

  /** 读缓存。sessionStorage 在隐私模式/禁用站点数据时会直接抛异常，必须 try 住 */
  function readSharedCache(handle) {
    try {
      var raw = window.sessionStorage.getItem(SHARED_KEY + handle);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  function writeSharedCache(handle, obj) {
    try {
      window.sessionStorage.setItem(SHARED_KEY + handle, JSON.stringify(obj));
    } catch (e) {
      /* 配额满或被禁用 —— 不影响功能，只是下个页面要重抓 */
    }
  }

  function bodiesFor(handle) {
    if (!handle) return null;
    if (!bodiesByHandle[handle]) {
      var cached = readSharedCache(handle);
      if (cached) bodiesByHandle[handle] = cached;
    }
    return bodiesByHandle[handle] || null;
  }

  /**
   * 去这个商品的详情页把抽屉 body 抓回来（入口②，见文件头）。
   * 只在内存与会话缓存都没有时才发；抓回来时用户若已换了商品，只缓存不落地。
   */
  function ensureSharedBodies(productUrl, handle) {
    if (!productUrl || !handle) return;
    if (bodiesFor(handle) || bodiesTried[handle]) return;
    bodiesTried[handle] = true;

    fetchText(productUrl)
      .then(function (html) {
        var found = harvestSharedBodies(parse(html));
        if (!found) throw new Error('no pdp drawers');
        bodiesByHandle[handle] = found;
        writeSharedCache(handle, found);
        if (handle !== currentHandle) return;
        sharedBodies = found;
        var host = qs('[data-amada-quickview-modals]');
        if (host) { applySharedBodies(host); syncDrawerContentFilters(); }
      })
      .catch(function () {
        /* 抓不到就保留兜底文案：这不是功能缺陷，只是内容退化。
           不重试 —— 重试只会在弱网下反复发 700KB 的整页请求。
           Size Guide / 小样入口没有依据时保持隐藏（打开只会是一个空弹窗）。 */
      });
  }

  /**
   * 主题的 page transition（theme.js:1396）在 document.body 上代理了所有 a[href]，
   * 200ms 后直接 window.location.href = target.href，**完全无视 preventDefault**。
   * 官方退出口是给该 <a> 加 no-transition 类。
   * 抽屉/弹窗里凡是「点了不该跳走」的锚点（href 是 # 或空的占位链接，
   * 多半来自运营在富文本里留的待填链接）都补上，否则点一下整页跳走、抽屉白开。
   */
  function guardAnchors(scope) {
    if (!scope) return;
    qsa('a[href="#"], a[href=""], a:not([href])', scope).forEach(function (a) {
      a.classList.add('no-transition');
    });
    externalizeAnchors(scope);
  }

  /**
   * 抽屉/弹窗里蓝色链接新窗口打开（运营返修 2026-09-14 #7 组 B）。
   * 只对「真的会离开当前页」的链接生效：站内锚点占位（#…）、javascript: 伪协议、
   * mailto:/tel: 都不新开窗口；已经带 target 的锚点保留原值。
   * 加了 target="_blank" 之后主题 theme.js:1398 的整页转场代理会自动放行
   * （已排除 [target="_blank"]），不需要额外处理。
   */
  function externalizeAnchors(scope) {
    if (!scope) return;
    qsa('a[href]', scope).forEach(function (a) {
      var href = a.getAttribute('href') || '';
      if (!href || href.charAt(0) === '#') return;
      if (/^(javascript|mailto|tel):/i.test(href)) return;
      if (!a.hasAttribute('target')) a.setAttribute('target', '_blank');
      var rel = (a.getAttribute('rel') || '').split(/\s+/).filter(Boolean);
      if (rel.indexOf('noopener') === -1) rel.push('noopener');
      if (rel.indexOf('noreferrer') === -1) rel.push('noreferrer');
      a.setAttribute('rel', rel.join(' '));
    });
  }

  /* ------------------------------------------------------------------
     优惠码「Copied!」状态 —— 与 PDP 共用同一个 localStorage 键，两边互通。
     PDP: assets/amada-redesign.js 的 COPIED_TEXT / COPIED_STORAGE_KEY。
     ------------------------------------------------------------------ */
  var COUPON_COPIED_TEXT = 'Copied!';
  var COUPON_COPIED_KEY = 'amadaCouponCopied';

  function couponAlreadyCopied(code) {
    if (!code) return false;
    try {
      return window.localStorage.getItem(COUPON_COPIED_KEY) === code;
    } catch (e) {
      /* 隐私模式下 localStorage 会抛异常 —— 静默降级为「只在本次页面内记住」 */
      return false;
    }
  }

  function markCopied(btn, code) {
    btn.textContent = COUPON_COPIED_TEXT;
    if (!code) return;
    try {
      window.localStorage.setItem(COUPON_COPIED_KEY, code);
    } catch (e) {
      /* 存不进就只保留本次页面内的文案 */
    }
  }

  /** 抽屉填完内容后同步一次：在 PDP 上已经复制过的券码，quick view 里也应显示 Copied! */
  function syncCouponCopied(scope) {
    var btn = qs('[data-amada-qv-copy]', scope || document);
    if (!btn) return;
    var code = btn.getAttribute('data-amada-qv-copy') || '';
    if (couponAlreadyCopied(code)) btn.textContent = COUPON_COPIED_TEXT;
  }

  /* ------------------------------------------------------------------
     Size Guide / Order Swatches 按当前商品过滤（2026-09-02 第五轮，运营验收 Q1）
     --------------------------------------------------------------------
     两块内容都是店铺级配置，且 quick view 里它们是从**某一个** PDP 页整段复制来的
     （契约 A），服务端根本不知道用户点开的是哪个商品 —— 所以只能在客户端按
     「当前 quick view 商品选中的型号/颜色」筛。规则与 PDP 共用
     assets/amada-drawer.js 的 AmadaDrawerContent，这里只负责取值。

     过滤范围是**弹窗宿主**而不是抽屉：弹窗内容挂在 body 末尾的
     [data-amada-quickview-modals] 里，而选项区在抽屉里，两者不是父子关系。
     ------------------------------------------------------------------ */
  function qvOptionState() {
    var root = drawerRoot();
    var out = { shape: '', layoutCount: 0, color: '', colorImage: '' };
    if (!root) return out;
    qsa('[data-amada-qv-option]', root).forEach(function (box) {
      var sel = qs('.amada-qv__swatch.is-selected', box);
      var val = sel ? sel.getAttribute('data-option-value') || '' : '';
      if (box.classList.contains('amada-qv__option--color')) {
        out.color = val;
        /* 色卡图：COLOR 方块的图挂在 button 的背景上（非颜色项才是内层 span） */
        var st = sel ? sel.getAttribute('style') || '' : '';
        var m = st.match(/background-image:\s*url\(([^)]+)\)/);
        if (m) out.colorImage = m[1].replace(/^["']|["']$/g, '');
      } else {
        out.shape = val;
        /* 当前页签下可见的方块数 = 该型号有几种布局款式（Size Guide 的兜底口径） */
        var n = 0;
        qsa('.amada-qv__swatch', box).forEach(function (b) {
          if (!b.hidden && b.getBoundingClientRect().width > 0) n++;
        });
        out.layoutCount = n;
      }
    });
    return out;
  }

  function syncDrawerContentFilters() {
    if (!window.AmadaDrawerContent) return;
    var host = qs('[data-amada-quickview-modals]');
    if (!host) return;
    var st = qvOptionState();
    window.AmadaDrawerContent.syncSizeGuide(host, st.shape, st.layoutCount);
    if (window.AmadaDrawerContent.syncDimensions) {
      window.AmadaDrawerContent.syncDimensions(host, st.shape);
    }
    window.AmadaDrawerContent.syncSwatches(host, st.color, st.colorImage);
    /* 过滤会把不匹配当前颜色的小样卡置 hidden；被藏起来的卡如果还勾着，
       底部按钮的「已选 N 张」就会把用户看不见的面料算进去（2026-09-14 多选）。*/
    qsa('[data-pdpd-swatch].is-selected[hidden]', host).forEach(function (n) {
      n.classList.remove('is-selected');
      n.setAttribute('aria-pressed', 'false');
    });
    /* Liquid 默认勾第一张，而第一张常常不匹配当前颜色（被上面清掉）——
       补选一张可见且能买的，保住"打开就有一张勾好"的体验（与 PDP 侧同一口径）。 */
    if (!qsa('[data-pdpd-swatch].is-selected', host).filter(function (n) { return !n.hidden; }).length) {
      var pick = qsa('[data-pdpd-swatch][data-variant-id]', host).filter(function (n) { return !n.hidden; })[0];
      if (pick) {
        pick.classList.add('is-selected');
        pick.setAttribute('aria-pressed', 'true');
      }
    }
    qsa('[data-pdpd-swatch-add]', host).forEach(function (btn) {
      qvSyncSwatchAdd(btn.getAttribute('data-swatch-group'), host);
    });
  }

  function loadPayload(productUrl, handle) {
    if (handle && cache[handle]) return Promise.resolve(cache[handle]);

    var staticUrl = withParams(productUrl, 'section_id=' + encodeURIComponent(QV_SECTION));
    var fullUrl = withParams(productUrl, 'view=quick-view');

    function take(box) {
      return qs('[data-amada-quickview-payload]', box);
    }

    return fetchText(staticUrl)
      .then(function (html) {
        var box = parse(html);
        var node = take(box);
        if (node) return node;
        /* 静态形态没给 payload（section 文件被改名 / Shopify 行为变化）→ 退回整页一次。
           不做第三次尝试：再失败就是真出问题了，让错误态显示出来比无限重试好。 */
        return fetchText(fullUrl).then(function (html2) {
          var box2 = parse(html2);
          var node2 = take(box2);
          if (!node2) throw new Error('payload not found');
          return node2;
        });
      })
      .catch(function (err) {
        /* 静态形态整个请求失败（404 / 超时）也退回整页，别直接进错误态 */
        return fetchText(fullUrl).then(function (html2) {
          var box2 = parse(html2);
          var node2 = take(box2);
          if (!node2) throw err;
          return node2;
        });
      })
      .then(function (node) {
        if (handle) cache[handle] = node;
        return node;
      });
  }

  /* ------------------------------------------------------------------
     填充抽屉
     ------------------------------------------------------------------ */
  /**
   * 页面上没有抽屉外壳时（只加载了 JS/CSS、没渲染 mode:'assets'），
   * 用 payload 里带的 <template data-amada-qv-shell> 现建一个。
   */
  function ensureShell(payload) {
    if (drawerRoot()) return;
    var tpl = qs('[data-amada-qv-shell]', payload);
    if (!tpl) return;
    var holder = document.createElement('div');
    holder.innerHTML = tpl.innerHTML;
    while (holder.firstChild) document.body.appendChild(holder.firstChild);
  }

  /* ------------------------------------------------------------------
     Seel「Worry-Free」横幅（运营 #31）—— 机制说明见文件头
     ------------------------------------------------------------------ */
  /** productId → 已经渲染好的 #seal-pdp-block 节点（React 树跟着节点走，可以直接搬回去） */
  var seelRendered = {};
  /** 给文档里「不是当前这一个」的旧占位改名时用的序号兜底（拿不到它的商品 id 时） */
  var seelRenameSeq = 0;
  /** 等横幅画出来的上限：10s / 300ms 轮询（MutationObserver 优先） */
  var SEEL_RENDER_TIMEOUT_MS = 10000;

  /**
   * 「横幅真的画出来了没」。
   * 🚨 不能只断 `.seal-pdp-widget-container` 存在 —— **占位里本来就有一个空的**，
   * 那样会在脚本还没跑完时就误判成完成。判据必须是「里面有东西了」。
   */
  function seelHasBanner(node) {
    if (!node) return false;
    if (qs('.seal-pdp-widget-outer, .seal-pdp-widget-title-text', node)) return true;
    var box = qs('.seal-pdp-widget-container', node);
    if (box && box.childElementCount > 0) return true;
    return node.childElementCount > 1;
  }

  /**
   * 把 Seel 横幅挂到 quick view 的 FAST & FREE SHIPPING 弹窗里。
   * @param {Element} scope     弹窗宿主 [data-amada-quickview-modals]
   * @param {string}  productId 当前 quick view 商品的 id（payload 的 data-product-id）
   */
  function mountSeel(scope, productId) {
    if (!scope || !productId) return;
    try {
      var fill = qs('[data-amada-qv-fill="shipping"]', scope);
      if (!fill) return;

      /* 占位是 applySharedBodies 每次 innerHTML 重新塞进来的一份**空的**副本。
         找不到 id=seal-pdp-block 有两种情况，都不是缺陷：
           · 本次已经挂过了（挂完 id 会改成 seal-pdp-block-<id>）→ 幂等返回；
           · PDP 根本没配 app block（运营没装 / 没加区块）→ 静默返回。 */
      var slot = qs('#seal-pdp-block', fill);
      if (!slot) return;

      /* 同一商品第二次打开：直接把上次渲染好的节点搬回来，**不再追加脚本**。 */
      var cachedNode = seelRendered[productId];
      if (cachedNode) {
        slot.replaceWith(cachedNode);
        return;
      }

      /* 真实脚本地址只能从搬过来的标记里读 —— CDN 路径带 hash 和版本号，
         Seel 一升级就会变，写死必挂（铁律：不写死第三方资源路径）。 */
      var srcEl = qs('script[type="module"][src*="pdp-widget"]', fill);
      var src = srcEl ? srcEl.getAttribute('src') : '';
      if (!src) return;

      seelRendered[productId] = slot;
      slot.setAttribute('data-amada-seel-product', productId);

      /* pdp-widget.js 用的是 getElementById —— 文档里同 id 有多个时只会命中第一个。
         先把其它的改名，保证这次模块执行一定落在当前这个占位上。 */
      qsa('[id="seal-pdp-block"]').forEach(function (el) {
        if (el === slot) return;
        var own = el.getAttribute('data-amada-seel-product');
        el.id = 'seal-pdp-block-' + (own || 'prev' + ++seelRenameSeq);
      });

      /* 组件挂载时只读一次 window.sealAppBlock.productId，必须在脚本执行前就位。 */
      window.sealAppBlock = {
        shopDomain: (window.Shopify && window.Shopify.shop) || '',
        productId: Number(productId),
        design_mode: false
      };

      var s = document.createElement('script');
      s.type = 'module';
      /* cache-bust 是**功能需要**不是防缓存：同一个模块 URL 浏览器只执行一次，
         带上不同 query 才会有新的模块实例去重新渲染当前这个占位。 */
      s.src = withParams(src, 'amada-qv=' + encodeURIComponent(productId));
      document.head.appendChild(s);

      /* 画出来之后把 id 让出去：下一个商品的新占位才拿得到 seal-pdp-block 这个 id。 */
      var done = false;
      var finish = function () {
        if (done || !seelHasBanner(slot)) return;
        done = true;
        externalizeAnchors(slot); // 横幅里的 Learn more 等链接也要新窗口打开
        slot.id = 'seal-pdp-block-' + productId;
      };
      if (typeof MutationObserver === 'function') {
        var mo = new MutationObserver(finish);
        mo.observe(slot, { childList: true, subtree: true });
        window.setTimeout(function () { mo.disconnect(); }, SEEL_RENDER_TIMEOUT_MS);
      }
      /* 轮询兜底：Seel 换成 shadow DOM 之类的形态时 MutationObserver 可能看不到。 */
      var waited = 0;
      var timer = window.setInterval(function () {
        waited += 300;
        finish();
        if (done || waited >= SEEL_RENDER_TIMEOUT_MS) window.clearInterval(timer);
      }, 300);
    } catch (err) {
      /* 只有真异常才打 —— 「没配 app block」走的是上面的静默返回，不到这里。 */
      console.error('[amada-quickview] Seel 横幅挂载失败', err);
    }
  }

  /** 当前 quick view 商品的 id（payload 的 data-product-id），给 mountSeel 用 */
  var currentProductId = null;

  function setHidden(el, hide) {
    if (hide) el.setAttribute('hidden', '');
    else el.removeAttribute('hidden');
  }

  /**
   * 入口显隐照抄 PDP（运营 10.5 #8：「PDP 有的 quick view 就需要有，PDP 没有这里也不需要有」）。
   * 依据是 harvestSharedBodies 记下的 __flags；还没抓到时 Size Guide / 小样入口先藏着。
   * Catalog 商品的弹窗内容由 payload 自带（data-amada-catalog-owned），不受这里管。
   */
  function syncSizeGuideEntry(scope) {
    var root = drawerRoot();
    if (!root) return;
    var host = qs('[data-amada-quickview-modals]') || scope;
    if (host && qs('[data-amada-catalog-owned]', host)) return;
    var flags = sharedBodies && sharedBodies.__flags;
    var showSize = Boolean(flags && flags.sizeGuide);
    var showSwatches = Boolean(flags && flags.swatches);
    var sizeEntries = qsa('[data-amada-modal-open="amada-qv-size-guide"]', root);
    /* 选项区已有 Size Guide 时，链接组里的兜底入口不再重复出（PDP 只有一个） */
    var hasOptionEntry = sizeEntries.some(function (el) {
      return !el.classList.contains('amada-qv__iconlink--size-guide-fallback');
    });
    sizeEntries.forEach(function (el) {
      var isFallback = el.classList.contains('amada-qv__iconlink--size-guide-fallback');
      setHidden(el, !showSize || (isFallback && hasOptionEntry));
    });
    qsa('[data-amada-modal-open="amada-qv-order-swatches"]', root).forEach(function (el) {
      setHidden(el, !showSwatches);
    });
    /* PDP 会把正文为空的链接收起（amada-pdp.js pruneEmptyLinks），这里同口径 */
    var empty = (flags && flags.empty) || {};
    ['product-overview', 'dimensions', 'material-care', 'shipping', 'returns-warranty'].forEach(function (key) {
      qsa('[data-amada-modal-open="amada-qv-' + key + '"]', root).forEach(function (el) {
        setHidden(el, Boolean(empty[key]));
      });
    });
  }

  /** 用运营配的那份内容（若抓到了）顶掉 Liquid 里的兜底文案 */
  function applySharedBodies(scope) {
    if (!sharedBodies) return;
    qsa('[data-amada-qv-fill]', scope).forEach(function (slot) {
      // Catalog payload already belongs to this product; global legacy bodies may belong to another SKU.
      if (slot.hasAttribute('data-amada-catalog-owned')) return;
      var key = slot.getAttribute('data-amada-qv-fill');
      if (!sharedBodies[key]) return;
      slot.innerHTML = sharedBodies[key];
      guardAnchors(slot);
    });
    /* 三处调用点都要挂 Seel，所以统一收在这里 —— 别在调用点各写一遍（会漏）。 */
    if (window.AmadaCatalog) window.AmadaCatalog.init(scope);
    mountSeel(scope, currentProductId);
    syncSizeGuideEntry(scope);
  }

  function fillDrawer(payload, handle) {
    ensureShell(payload);
    /* 🚨 必须在 applySharedBodies 之前赋值：Seel 横幅挂载要用它，
       而横幅是在 applySharedBodies 末尾统一挂的（三处调用点都覆盖到）。 */
    currentProductId = payload.getAttribute('data-product-id') || null;
    /* 换商品必须同步切内容：只用**这个商品自己**的 PDP 抽屉内容，没有就等 ensureSharedBodies */
    currentHandle = handle;
    sharedBodies = bodiesFor(handle);
    var root = drawerRoot();
    if (!root) return;

    var body = qs('.amada-drawer__body', root);
    var footer = qs('.amada-drawer__footer', root);

    var srcBody = qs('[data-amada-qv-body]', payload);
    var srcFooter = qs('[data-amada-qv-footer]', payload);
    var srcModals = qs('[data-amada-qv-modals]', payload);
    var srcVariants = qs('[data-amada-qv-variants]', payload);

    /* 换商品会把整块 body 换掉。先把上一块评论区拆干净：
       amada-reviews.js 的下拉「点外面关闭」和 ESC 是挂在 document 上的，
       不回收的话每换一次商品多一份，表现是「ESC 一次触发多次 / 下拉行为诡异」。 */
    if (window.AmadaReviews) window.AmadaReviews.destroy(root);

    if (body && srcBody) body.innerHTML = srcBody.innerHTML;
    if (footer && srcFooter) footer.innerHTML = srcFooter.innerHTML;

    /* 弹窗挂到 body 末尾的宿主里 —— 不放在抽屉内部：
       抽屉面板有自己的层叠上下文（transform + z-index），
       弹窗放进去会被抽屉的边界裁掉，永远盖不到抽屉之上。 */
    var host = qs('[data-amada-quickview-modals]');
    if (host && srcModals) {
      host.innerHTML = srcModals.innerHTML;
      host.hidden = false;
      applySharedBodies(host);
      /* 弹窗内容是异步搬进来的：内容一落地就按当前商品过滤一次，
         否则先开弹窗的用户看到的是未过滤的全量小样/尺寸图（运营验收 Q1 的现象）。 */
      syncDrawerContentFilters();
      guardAnchors(host);
    }

    variants = [];
    if (srcVariants) {
      try {
        variants = JSON.parse(srcVariants.textContent) || [];
      } catch (err) {
        variants = [];
      }
    }

    currentHandle = handle;
    if (body) body.scrollTop = 0;

    guardAnchors(body);
    syncTabsToSelected(body);
    /* Liquid 与选中态都以 selected_or_first_available_variant 起步；仍在内容落地后
       主动应用一次已选组合，让价格、footer variant id、库存事件使用同一个真实 variant。
       variants 解析失败时不覆盖服务端价格/表单，单变体商品则自然命中数组第一项。 */
    var initialRoot = qs('[data-amada-qv-root]', body);
    var initialVariant = initialRoot ? findVariant(selectedOptionValues(initialRoot)) : null;
    if (initialRoot && initialVariant) applyVariant(initialRoot, initialVariant);
    syncCouponCopied(body);
    syncFav(initialVariant ? initialVariant.id : null);
    setDrawerTitle(TITLE_BASE);

    /* ── 复用 PDP 的两个模块（2026-09-02 第二轮）──────────────────────
       两者的 JS 都只在 DOMContentLoaded / shopify:section:load 扫一次 DOM，
       AJAX 插进来的节点它们扫不到 —— 必须在这里主动挂载。
       资源本身由分类页整页加载（innerHTML 插入的 <script src> 不执行）。 */

    /* 库存三态的作用域标记：amada-back-in-stock.js 原来只认 `.product` 祖先，
       抽屉里没有这个类。状态行在 footer 里、要搬到 body 的空槽里，
       所以作用域必须打在**同时包住 body 和 footer** 的抽屉根上。
       打在这里而不是 amada-drawer.liquid：那是共用件，不为一个页面改它。 */
    root.setAttribute('data-amada-restock-scope', '');
    if (window.AmadaRestock) window.AmadaRestock.init();

    if (window.AmadaReviews) window.AmadaReviews.boot(root);
    syncDrawerContentFilters();
    syncSizeGuideEntry(root);

    /* 走了 ensureShell 那条降级路径时，抽屉是刚建出来的、还没打开 —— 补一次。
       正常路径下 openQuickView 已经打开过，AmadaDrawer.open 里有「已开着直接返回」的短路。 */
    if (window.AmadaDrawer && !window.AmadaDrawer.isOpen(DRAWER_ID)) {
      window.AmadaDrawer.open(DRAWER_ID);
    }
  }

  function showError() {
    var root = drawerRoot();
    if (!root) return;
    var skeleton = qs('[data-amada-qv-skeleton]', root);
    var error = qs('[data-amada-qv-error]', root);
    if (skeleton) skeleton.hidden = true;
    if (error) error.hidden = false;
  }

  function resetToSkeleton() {
    var root = drawerRoot();
    if (!root) return;
    var body = qs('.amada-drawer__body', root);
    var footer = qs('.amada-drawer__footer', root);
    if (body) {
      body.innerHTML =
        '<div class="amada-qv" data-amada-qv-root>' +
        '<div class="amada-qv__skeleton" data-amada-qv-skeleton>' +
        '<div class="amada-qv__skeleton-media"></div>' +
        '<div class="amada-qv__skeleton-line amada-qv__skeleton-line--title"></div>' +
        '<div class="amada-qv__skeleton-line"></div>' +
        '<div class="amada-qv__skeleton-line amada-qv__skeleton-line--short"></div>' +
        '</div>' +
        '<p class="amada-qv__error" data-amada-qv-error hidden>Sorry, we couldn’t load this product. Please try again.</p>' +
        '</div>';
      body.scrollTop = 0;
    }
    if (footer) footer.innerHTML = '<div class="amada-qv__actions" data-amada-qv-actions></div>';
  }

  /* ------------------------------------------------------------------
     打开入口（契约 B：只认 [data-amada-quickview-open]）
     ------------------------------------------------------------------ */
  function openQuickView(trigger) {
    /* ⚠️ 这里**不能**因为「页面上没有抽屉外壳」就早退。
       外壳正常由分类页的 {% render 'amada-quickview-drawer' %} 提供，
       但也支持从 AJAX payload 里的 <template data-amada-qv-shell> 现建（见 ensureShell）。
       早退过一次，表现是「点了完全没反应、连请求都不发」，很容易误判成事件没绑上。 */
    var root = drawerRoot();

    var handle = trigger.getAttribute('data-product-handle') || '';
    var url = trigger.getAttribute('data-product-url') || '';
    if (!url && handle) url = window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root + 'products/' + handle
      : '/products/' + handle;
    if (!url) return;

    /* 换商品才重置骨架：同一商品再点开时内容已在 DOM 里，直接开，不闪一下白。
       外壳还不存在时没什么可重置的，跳过。 */
    if (root && handle !== currentHandle) resetToSkeleton();

    // 先开抽屉再请求 —— 否则用户点完要干等一次网络往返才看到反应
    if (root && window.AmadaDrawer) {
      window.AmadaDrawer.open(DRAWER_ID);
      setDrawerTitle(TITLE_BASE);
    }

    if (handle && handle === currentHandle && cache[handle]) return;

    var requestedHandle = handle;
    loadPayload(url, handle)
      .then(function (payload) {
        /* 抢答保护：慢请求回来时用户可能已经关掉抽屉、或点开了别的商品。
           不比对就会把上一个商品的内容盖到当前这个上（经典图片错位同源问题）。 */
        /* 抢答保护：慢请求回来时用户可能已经关掉抽屉、或点开了别的商品。
           注意 drawerRoot() 可能是空的（走 ensureShell 那条路），此时不能用 isOpen 判断。 */
        if (drawerRoot() && window.AmadaDrawer && !window.AmadaDrawer.isOpen(DRAWER_ID)) return;
        if (requestedHandle && requestedHandle !== lastRequestedHandle) return;
        fillDrawer(payload, requestedHandle);
        // 用户已经明确点开了 quick view，这时候再去补弹窗内容（会话内只补一次）
        ensureSharedBodies(url, requestedHandle);
      })
      .catch(function () {
        if (requestedHandle && requestedHandle !== lastRequestedHandle) return;
        showError();
      });
  }

  /** 最后一次点击请求的 handle —— 用于抢答保护（见上） */
  var lastRequestedHandle = null;

  /* ------------------------------------------------------------------
     抽屉标题两态（稿：基础 QUICK VIEW，任一弹窗打开后 PRODUCT DETAILS）
     ------------------------------------------------------------------ */
  function setDrawerTitle(text) {
    var root = drawerRoot();
    if (!root) return;
    var title = qs('.amada-drawer__title', root);
    if (title) title.textContent = text;
  }

  /** 只对 quick view 自己的弹窗（id 以 amada-qv- 开头）联动标题，别管别处的弹窗 */
  function isQuickViewModal(id) {
    return typeof id === 'string' && id.indexOf('amada-qv-') === 0;
  }

  document.addEventListener('amada:modal:open', function (e) {
    if (!isQuickViewModal(e.detail && e.detail.id)) return;
    if (window.AmadaDrawer && !window.AmadaDrawer.isOpen(DRAWER_ID)) return;
    setDrawerTitle(TITLE_DETAILS);
  });

  document.addEventListener('amada:modal:close', function (e) {
    if (!isQuickViewModal(e.detail && e.detail.id)) return;
    /* 关掉的可能只是叠在上层的 learn more，下面还有 PRODUCT OVERVIEW 开着 →
       只有一个弹窗都不剩时才换回 QUICK VIEW。事件是在 close() 里派发的，
       此时栈已经出队，所以这里的 isOpen() 是准确的。 */
    if (window.AmadaModal && window.AmadaModal.isOpen()) return;
    setDrawerTitle(TITLE_BASE);
  });

  /* 抽屉关闭时把弹窗一起收掉：否则先开弹窗、再点抽屉遮罩，会剩一个孤零零的弹窗 */
  document.addEventListener('amada:drawer:close', function (e) {
    if (!e.detail || e.detail.id !== DRAWER_ID) return;
    if (window.AmadaModal) window.AmadaModal.closeAll();
    /* 评论区的 document 级监听也一并回收（DOM 留着不动 ——
       同一商品再打开时 openQuickView 会短路、不重新填充，重新 boot 即可）。 */
    var root = drawerRoot();
    if (root && window.AmadaReviews) window.AmadaReviews.destroy(root);
  });

  /* 再次打开同一商品时 fillDrawer 不会执行（内存缓存短路），这里补挂一次。 */
  document.addEventListener('amada:drawer:open', function (e) {
    if (!e.detail || e.detail.id !== DRAWER_ID) return;
    var root = drawerRoot();
    if (!root) return;
    if (window.AmadaReviews) window.AmadaReviews.boot(root);
    if (window.AmadaRestock && root.hasAttribute('data-amada-restock-scope')) window.AmadaRestock.init();
  });

  /* ------------------------------------------------------------------
     评分行回填（稿 1:250）
     服务端只从 product.metafields.judgeme.badge 取到初值（可能取不到）；
     评论模块拉到 Judge.me 的真实数据后广播 amada:reviews-loaded，这里回填 ——
     **两处同源**，不会出现「上面 4.9、下面 4.8」这种自相矛盾。
     ------------------------------------------------------------------ */
  document.addEventListener('amada:reviews-loaded', function (e) {
    var d = e.detail || {};
    var root = drawerRoot();
    if (!root) return;
    /* 🚨 只认**当前抽屉里那一块**发出的事件。
       换商品时上一块评论区的请求可能还在路上，晚到的事件带着上一个商品的
       average/total —— 不校验来源就会把 0 评论商品的结果盖到当前商品头上
       （实测表现：52 条评论那件的评分行被又藏了回去）。 */
    if (!d.root || !root.contains(d.root)) return;
    var row = qs('[data-amada-qv-rating]', root);
    if (!row) return;

    var total = Number(d.total) || 0;
    if (!total) { row.hidden = true; return; }

    var avg = Number(d.average) || 0;
    var score = qs('[data-amada-qv-rating-score]', row);
    var stars = qs('[data-amada-qv-rating-stars]', row);
    var count = qs('[data-amada-qv-rating-count]', row);
    /* 与 amada-reviews.js renderSummary 同一口径的一位小数，避免两处四舍五入不同 */
    if (score) score.textContent = String(Math.round(avg * 10) / 10);
    if (stars) stars.style.setProperty('--amada-qv-stars', (avg * 20) + '%');
    if (count) count.textContent = total + ' review' + (total === 1 ? '' : 's');
    row.hidden = false;
  });

  /* ------------------------------------------------------------------
     OPTION 页签筛选（稿 1:282）
     数据层完全不动 —— 页签只是展示层分组，点它只管方块显隐，不改变体选择。
     ------------------------------------------------------------------ */
  function applyTabs(tabsEl, group) {
    var wrap = tabsEl.parentElement;
    if (!wrap) return;

    qsa('.amada-qv__swatch[data-amada-qv-group]', wrap).forEach(function (sw) {
      var g = sw.getAttribute('data-amada-qv-group') || '';
      sw.hidden = group ? g !== group : false;
    });

    qsa('[data-amada-qv-tab]', tabsEl).forEach(function (tab) {
      var on = tab.getAttribute('data-amada-qv-tab') === group;
      tab.classList.toggle('is-active', on);
      tab.setAttribute('aria-selected', on ? 'true' : 'false');
    });
  }

  /**
   * 初始页签：跟着**当前已选变体**走，不是永远选第一个。
   * 否则当前选中的方块可能正好被筛掉，看起来像「什么都没选」。
   */
  function syncTabsToSelected(scope) {
    qsa('[data-amada-qv-tabs]', scope || document).forEach(function (tabsEl) {
      var wrap = tabsEl.parentElement;
      if (!wrap) return;
      var selected = qs('.amada-qv__swatch.is-selected[data-amada-qv-group]', wrap);
      var first = qs('[data-amada-qv-tab]', tabsEl);
      var group = selected
        ? selected.getAttribute('data-amada-qv-group')
        : first
          ? first.getAttribute('data-amada-qv-tab')
          : '';
      if (group) applyTabs(tabsEl, group);
    });
  }

  /* ------------------------------------------------------------------
     变体切换
     主题的 Product 组件只在页面加载时按 [data-section-type] 初始化，
     AJAX 插进来的 DOM 它不认 —— 所以这里自带一套最小实现（选项 → 变体匹配）。
     ------------------------------------------------------------------ */
  function selectedOptionValues(root) {
    var out = [];
    qsa('[data-amada-qv-option]', root).forEach(function (group) {
      var sel = qs('.amada-qv__swatch.is-selected', group);
      var pos = parseInt(group.getAttribute('data-amada-qv-option').replace('option', ''), 10);
      out[pos - 1] = sel ? sel.getAttribute('data-option-value') : null;
    });
    return out;
  }

  function findVariant(values) {
    for (var i = 0; i < variants.length; i++) {
      var v = variants[i];
      var ok = true;
      for (var j = 0; j < values.length; j++) {
        if (values[j] == null) continue;
        if (v.options[j] !== values[j]) { ok = false; break; }
      }
      if (ok) return v;
    }
    return null;
  }

  function applyVariant(root, variant) {
    var priceEl = qs('[data-amada-qv-price]', root);
    var compareEl = qs('[data-amada-qv-compare]', root);
    var skuEl = qs('[data-amada-qv-sku]', root);
    var skuVal = skuEl ? qs('.amada-qv__sku-value', skuEl) : null;
    var media = qs('[data-amada-qv-media]', root);
    /* body 与 footer 是 sibling，root 只包 body。所有表单节点必须在当前 drawer 内找，
       既能跨到 footer，也不会误抓页面上别的 product form。 */
    var drawer = root && root.closest
      ? root.closest('[data-amada-drawer="' + DRAWER_ID + '"]')
      : null;
    var formScope = drawer || document;
    var idInput = qs('[data-amada-qv-variant-id]', formScope);
    var atc = qs('[data-amada-qv-atc]', formScope);
    var atcLabel = qs('[data-amada-qv-atc-label]', formScope);

    if (!variant) {
      if (idInput) idInput.value = '';
      if (atc) atc.disabled = true;
      if (atcLabel) atcLabel.textContent = 'Unavailable';
      document.dispatchEvent(new CustomEvent('product:variant-change', {
        detail: { variant: null, amadaScope: 'qv' }
      }));
      syncDrawerContentFilters();
      return;
    }

    if (priceEl) priceEl.innerHTML = variant.price;
    if (!compareEl && variant.compare && priceEl && priceEl.parentNode) {
      compareEl = document.createElement('s');
      compareEl.className = 'amada-qv__price-was';
      compareEl.setAttribute('data-amada-qv-compare', '');
      priceEl.parentNode.appendChild(compareEl);
    }
    if (compareEl) {
      // 可能是折扣展示临时填过的 compare；变体数据重新取得所有权后先摘掉该标记。
      compareEl.removeAttribute('data-amada-filled');
      compareEl.innerHTML = variant.compare || '';
      compareEl.hidden = !variant.compare;
    }
    if (skuEl) {
      skuEl.hidden = !variant.sku;
      if (skuVal) skuVal.textContent = variant.sku || '';
    }
    if (media && variant.media) {
      media.src = variant.media;
    }
    if (idInput) idInput.value = variant.id;
    syncFav(variant.id);
    if (atc) atc.disabled = !variant.available;
    if (atcLabel) atcLabel.textContent = variant.available ? 'Add to Cart' : 'Sold Out';

    /* 价格与 footer variant id 都写完后再广播。折扣脚本据 amadaScope 只刷新 QV，
       不会把同页背景 PDP 仍是折后价的 DOM 当成新原价再次扣减。 */
    document.dispatchEvent(new CustomEvent('product:variant-change', {
      detail: { variant: variant, amadaScope: 'qv' }
    }));
    /* 选了别的型号/颜色 → Size Guide 与小样要跟着换（运营验收 Q1） */
    syncDrawerContentFilters();
  }

  function onSwatchClick(swatch) {
    var group = swatch.closest('[data-amada-qv-option]');
    var root = swatch.closest('[data-amada-qv-root]');
    if (!group || !root) return;

    qsa('.amada-qv__swatch', group).forEach(function (s) {
      var on = s === swatch;
      s.classList.toggle('is-selected', on);
      s.setAttribute('aria-pressed', on ? 'true' : 'false');
    });

    // 没有页签的那一组，标签行显示当前选中值（稿 1:336 的 IVORY）
    var label = qs('[data-amada-qv-option-label]', group);
    if (label) label.textContent = swatch.getAttribute('data-option-value') || '';

    applyVariant(root, findVariant(selectedOptionValues(root)));
  }

  /* ------------------------------------------------------------------
     加购
     JS 失效时表单会真的 POST 到 /cart/add 并跳 /cart，功能不丢；
     有 JS 时走 /cart/add.js 再开购物袋抽屉。

     🚨 2026-08-27 返修（figma-qa/reports/audit-cart-favorites.md A-2）：
     这里原来自己抄了一份「点 header 购物袋 → 400ms 后看抽屉开没开」，
     而探测开态用的是 `.quick-cart.is-active / [aria-hidden="false"] / .quick-cart--visible`
     —— 主题真实的开态标记只有 `.quick-cart__wrapper.active`，三个选择器一个都命不中，
     于是 **加购成功后必然在 400ms 后 window.location.href='/cart'**，
     用户被强制整页跳走（实测：/cart.js item_count 0→1 后 3.25s 主框架导航到 /cart）。

     现在统一走 sections/quick-cart.liquid 导出的 window.AmadaCartDrawer，
     一份实现两处用，且等待改成 MutationObserver「等到真的开」而不是猜 400ms。
     拿不到 API（运营关掉了 quick-cart section）才跳 /cart —— 那才是该跳的场景。
     ------------------------------------------------------------------ */
  /* ------------------------------------------------------------------
     2026-10-05 运营 10.5 #8：数量框 + 收藏心形（PDP 有的 quick view 也要有）
     ------------------------------------------------------------------ */
  /** 当前数量：非法输入一律回落 1，不让 0 / 负数 / 小数进购物车 */
  function qvQuantity() {
    var root = drawerRoot();
    var input = root ? qs('[data-amada-qv-qty-input]', root) : null;
    var n = input ? parseInt(input.value, 10) : 1;
    return n >= 1 ? n : 1;
  }

  /** 心形绑定当前变体，并按收藏状态点亮（全站委托只在点击/变化时刷新，这里补首次） */
  function syncFav(variantId) {
    var root = drawerRoot();
    var fav = root ? qs('[data-amada-qv-fav]', root) : null;
    if (!fav) return;
    if (variantId) fav.setAttribute('data-variant-id', String(variantId));
    var api = window.AmadaFavorites;
    if (!api || typeof api.has !== 'function') return;
    var on = api.has(fav.getAttribute('data-product-handle'), fav.getAttribute('data-variant-id'));
    fav.classList.toggle('is-favorited', on);
    fav.setAttribute('aria-pressed', on ? 'true' : 'false');
  }

  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest ? e.target.closest('[data-amada-qv-qty-step]') : null;
    if (!btn) return;
    var box = btn.closest('[data-amada-qv-qty]');
    var input = box ? qs('[data-amada-qv-qty-input]', box) : null;
    if (!input) return;
    var n = parseInt(input.value, 10);
    if (!(n >= 1)) n = 1;
    n += parseInt(btn.getAttribute('data-amada-qv-qty-step'), 10) || 0;
    input.value = String(n < 1 ? 1 : n);
  });

  document.addEventListener('change', function (e) {
    var input = e.target && e.target.matches && e.target.matches('[data-amada-qv-qty-input]') ? e.target : null;
    if (!input) return;
    var n = parseInt(input.value, 10);
    input.value = String(n >= 1 ? n : 1);
  });

  function openCartDrawer() {
    if (window.AmadaDrawer) window.AmadaDrawer.close(DRAWER_ID);
    if (window.AmadaCartDrawer && typeof window.AmadaCartDrawer.open === 'function') {
      window.AmadaCartDrawer.open({ justAdded: true });
      return;
    }
    window.location.href = '/cart';
  }

  function onSubmit(form, e) {
    if (typeof fetch !== 'function') return; // 没有 fetch 就让表单原样提交
    e.preventDefault();

    var btn = qs('[data-amada-qv-atc]', form);
    var idInput = qs('[data-amada-qv-variant-id]', form);
    if (!idInput || !idInput.value) return;

    if (btn) {
      btn.classList.add('is-loading');
      btn.disabled = true;
    }

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(idInput.value), quantity: qvQuantity() }] })
    })
      .then(function (r) {
        if (!r.ok) throw new Error('add failed');
        return r.json();
      })
      .then(function () {
        /* 2026-10-05 运营 10.5 #11：裸 fetch 加购不会触发主题的 cart:updated，
           assets/amada-auto-discount.js 收不到通知 → 快速预览里显示的券码进车后不自动挂。
           这里补派发一次（主题内部走自己的 pub-sub，DOM 上的这个事件只有挂码脚本在听）。 */
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: null, source: 'amada-quickview' } }));
        openCartDrawer();
      })
      .catch(function () {
        // 加购失败要可感知，别静默（铁律 31：先分「代码 bug」还是「网络不可达」）
        if (btn) {
          btn.classList.add('is-error');
          window.setTimeout(function () { btn.classList.remove('is-error'); }, 2000);
        }
      })
      .finally(function () {
        if (btn) {
          btn.classList.remove('is-loading');
          btn.disabled = false;
        }
      });
  }

  /* ------------------------------------------------------------------
     事件委托（全部捕获阶段，理由见文件头）
     ------------------------------------------------------------------ */
  document.addEventListener(
    'click',
    function (e) {
      var t = e.target;
      if (!t || !t.closest) return;

      // ① quick view 触发器（契约 B）
      var opener = t.closest('[data-amada-quickview-open]');
      if (opener) {
        e.preventDefault();
        if (e.stopImmediatePropagation) e.stopImmediatePropagation();
        lastRequestedHandle = opener.getAttribute('data-product-handle') || null;
        openQuickView(opener);
        return;
      }

      // 以下只处理抽屉内部的交互
      var tab = t.closest('[data-amada-qv-tab]');
      if (tab) {
        var tabsEl = tab.closest('[data-amada-qv-tabs]');
        if (tabsEl) {
          e.preventDefault();
          applyTabs(tabsEl, tab.getAttribute('data-amada-qv-tab'));
          return;
        }
      }

      var swatch = t.closest('[data-amada-qv-swatch]');
      if (swatch) {
        e.preventDefault();
        onSwatchClick(swatch);
        return;
      }

      var copy = t.closest('[data-amada-qv-copy]');
      if (copy) {
        e.preventDefault();
        var code = copy.getAttribute('data-amada-qv-copy') || '';
        var done = function () {
          /* 2026-09-02 第四轮（设计验收）：与 PDP 完全对齐 ——
             PDP（assets/amada-redesign.js:390~425）**只换文案、不换颜色**，
             文案是 `Copied!`，用 localStorage 键 amadaCouponCopied 记住券码。
             原来这里加 .is-copied 把蓝链变成黑字，是 quick view 自己发明的，
             与 PDP 不一致 —— 变色规则已从 amada-quickview.css 删除。
             ⚠️ 键名与取值口径必须与 PDP 一字不差，否则两边状态不互通
             （PDP 存的是**券码本身**，不是布尔；运营换券码即自动重置）。 */
          markCopied(copy, code);
        };
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(code).then(done, function () {});
        } else {
          // 老浏览器兜底：临时 textarea + execCommand
          var ta = document.createElement('textarea');
          ta.value = code;
          ta.setAttribute('readonly', '');
          ta.style.position = 'absolute';
          ta.style.left = '-9999px';
          document.body.appendChild(ta);
          ta.select();
          try { document.execCommand('copy'); done(); } catch (err) {}
          document.body.removeChild(ta);
        }
      }
    },
    true
  );

  document.addEventListener(
    'submit',
    function (e) {
      var form = e.target && e.target.closest ? e.target.closest('[data-amada-qv-form]') : null;
      if (form) onSubmit(form, e);
    },
    true
  );

  /* ==================================================================
     ORDER FREE SWATCHES 弹窗的交互（2026-08-27 补）

     🚨 缺陷：quick view 里点小样卡 / ADD TO CART **完全没反应**。
     根因不在样式也不在选择器，而是**这段交互根本没被加载**：
     它原本只写在 `sections/amada-pdp-drawers.liquid` 的内联 <script> 里，
     而**分类页不渲染那个 section** → quick view 弹窗里 DOM 有、JS 一行都没有。
     （弹窗内容是 applySharedBodies 用 innerHTML 整段塞进来的，
      「靠内联 script 绑定的交互」在 quick view 侧一律会失效 —— 这是机制风险，
      不是这一个弹窗的偶发问题。已复查其余 6 个弹窗：overview / size-guide /
      dimensions / material-care / shipping / returns-warranty 都是纯静态内容
      加 learn more 链接，learn more 自带 data-amada-modal-open 由 amada-modal.js
      的 document 级委托接管，没有同类依赖。）

     实现要点：
     · 全部 document 级委托 —— 弹窗内容是 AJAX innerHTML 进来的，绑元素必掉。
     · 只处理落在弹窗（.amada-modal）里的点击；PDP 抽屉里的那份仍归 section 脚本管。
     · PDP 上两份脚本会同时在页面里 → 用 window.__amadaPdpDrawersInit 让位，
       否则同一次点击会加购两次（那个标志由 section 脚本在解析时就置上，
       点击发生时一定已经落定，不存在竞态）。
     ================================================================== */
  function qvSwatchScope(el) {
    return el.closest('.amada-modal, [data-amada-modal]');
  }

  /* 面料详情层用 position:absolute + right:720（PDP 稿是贴在 720 抽屉左侧的一整列），
     它假设最近的定位祖先是 .amada-drawer（fixed inset:0）。在弹窗里最近的定位祖先
     是弹窗面板 → 会被推到面板外面看不见。这里把它挂到 .amada-modal 根节点
     （同样是 fixed inset:0），移动端（居中白卡 + 遮罩）与稿一致；
     ⚠️ PC 下的横向位置仍是按"抽屉左侧"算的，与 quick view 的居中弹窗对不上 ——
        这属于稿没画的组合，需要 amada-quickview.css / amada-pdp-drawers.css 里
        补一条 scoped 覆盖，两个文件都不在本轮可改清单内，已在交付报告标为待办。 */
  function qvMountFabricLayer(scope) {
    var layer = qs('[data-pdpd-fabric-layer]', scope);
    if (!layer || layer.parentElement === scope) return layer;
    scope.appendChild(layer);
    return layer;
  }

  function qvCloseFabric(scope) {
    var layer = qs('[data-pdpd-fabric-layer]', scope);
    if (!layer) return;
    layer.hidden = true;
    qsa('[data-pdpd-fabric-panel]', layer).forEach(function (p) { p.hidden = true; });
  }

  function qvOpenFabric(scope, index) {
    var layer = qvMountFabricLayer(scope);
    if (!layer) return;
    var target = qs('[data-pdpd-fabric-panel="' + index + '"]', layer);
    if (!target) return;
    qsa('[data-pdpd-fabric-panel]', layer).forEach(function (p) { p.hidden = p !== target; });
    layer.hidden = false;
    target.scrollTop = 0;
  }

  /* 多选（2026-09-14 拍板 2）：与 sections/amada-pdp-drawers.liquid 里的
     selectedSwatches / syncSwatchAdd 同一口径 —— 只统计可见（未被颜色过滤 hidden）的卡，
     一张样品一条 item，各自带 `_swatch_image` / `_swatch_name` 属性，
     购物车行据此显示面料图与面料名。两处必须一起改，否则 PDP 与 quick view 行为分叉。 */
  function qvSelectedSwatches(group, scope) {
    return qsa('[data-pdpd-swatch].is-selected[data-swatch-group="' + group + '"]', scope)
      .filter(function (n) { return !n.hidden; });
  }

  function qvSyncSwatchAdd(group, scope) {
    var btn = qs('[data-pdpd-swatch-add][data-swatch-group="' + group + '"]', scope);
    if (!btn) return;
    var selected = qvSelectedSwatches(group, scope);
    var ok = selected.length > 0 && selected.every(function (n) {
      return !!n.getAttribute('data-variant-id');
    });
    btn.disabled = !ok;
    btn.setAttribute('aria-disabled', ok ? 'false' : 'true');
    if (!ok) {
      btn.textContent = btn.getAttribute('data-unavailable-label') || 'Sample unavailable';
      return;
    }
    var countLabel = btn.getAttribute('data-count-label');
    btn.textContent = countLabel
      ? countLabel.replace('{count}', String(selected.length))
      : (btn.getAttribute('data-available-label') || 'Add to cart');
  }

  function qvAddSwatchToCart(btn, scope) {
    var group = btn.getAttribute('data-swatch-group');
    var selected = qvSelectedSwatches(group, scope);
    var items = [];
    for (var i = 0; i < selected.length; i++) {
      var vid = selected[i].getAttribute('data-variant-id');
      /* 未绑定商品的小样卡只做选中态，不发起加购（与 PDP 侧同一口径） */
      if (!vid) return;
      items.push({
        id: Number(vid),
        quantity: 1,
        properties: {
          _swatch_image: selected[i].getAttribute('data-swatch-image') || '',
          _swatch_name: selected[i].getAttribute('data-swatch-name') || ''
        }
      });
    }
    if (!items.length) return;

    /* 铁律 31：任何 fetch 都必须带超时，否则失败表现成无限转圈、看起来像 bug */
    var controller = window.AbortController ? new AbortController() : null;
    var timer = window.setTimeout(function () { if (controller) controller.abort(); }, 8000);

    btn.disabled = true;
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items }),
      signal: controller ? controller.signal : undefined
    })
      .then(function (r) { if (!r.ok) throw new Error('add failed'); return r.json(); })
      .then(function () {
        if (window.AmadaModal) window.AmadaModal.close();
        openCartDrawer();
      })
      .catch(function () {
        btn.classList.add('is-error');
        window.setTimeout(function () { btn.classList.remove('is-error'); }, 2000);
      })
      .finally(function () {
        window.clearTimeout(timer);
        btn.disabled = false;
      });
  }

  document.addEventListener('click', function (e) {
    if (window.__amadaPdpDrawersInit) return; // PDP 上由 section 脚本接管，避免双重加购
    var t = e.target;
    if (!t || !t.closest) return;

    var closeBtn = t.closest('[data-pdpd-fabric-close]');
    if (closeBtn) {
      var s0 = qvSwatchScope(closeBtn);
      if (!s0) return;
      e.preventDefault();
      qvCloseFabric(s0);
      return;
    }

    var addBtn = t.closest('[data-pdpd-swatch-add]');
    if (addBtn) {
      var s1 = qvSwatchScope(addBtn);
      if (!s1) return;
      e.preventDefault();
      qvAddSwatchToCart(addBtn, s1);
      return;
    }

    var card = t.closest('[data-pdpd-swatch]');
    if (card) {
      var s2 = qvSwatchScope(card);
      if (!s2) return;
      var g = card.getAttribute('data-swatch-group');
      var sibs = qsa('[data-pdpd-swatch][data-swatch-group="' + g + '"]', s2);
      var idx = 0;
      sibs.forEach(function (n, i) { if (n === card) idx = i; });
      /* 多选：点一下切换这一张，其余不动；没绑定商品的卡不能勾，但能点开面料详情 */
      var picked = false;
      if (card.getAttribute('data-variant-id')) {
        picked = !card.classList.contains('is-selected');
        card.classList.toggle('is-selected', picked);
        card.setAttribute('aria-pressed', picked ? 'true' : 'false');
        qvSyncSwatchAdd(g, s2);
      }
      if (picked || !card.getAttribute('data-variant-id')) {
        qvOpenFabric(s2, idx); // 勾上 / 看不能买的卡 = 展开面料详情（稿的二级态）
      } else {
        qvCloseFabric(s2);
      }
      return;
    }

    /* 移动端面料详情是遮罩上的居中白卡 → 点遮罩空白处关掉 */
    if (t.hasAttribute && t.hasAttribute('data-pdpd-fabric-layer')) {
      var s3 = qvSwatchScope(t);
      if (s3) qvCloseFabric(s3);
    }
  }, false);

  /* ESC：先收面料详情。不 stopPropagation —— 一次 ESC 连弹窗一起关，与"✕ 收全部"同意。 */
  document.addEventListener('keydown', function (e) {
    if (window.__amadaPdpDrawersInit) return;
    if (e.key !== 'Escape' && e.keyCode !== 27) return;
    qsa('.amada-modal [data-pdpd-fabric-layer], [data-amada-modal] [data-pdpd-fabric-layer]').forEach(function (layer) {
      var s = layer.closest('.amada-modal, [data-amada-modal]');
      if (s) qvCloseFabric(s);
    });
  });
})();
